import React, { useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import CartLoader from '../../../../Helper/Loaders/CartLoader';
import { useTranslation } from 'react-i18next';
import { calculateDynamicRange, getTooltipHtml, swapIndexForMultiYAxis } from '../../../../Helper/DigitalTwin/DigitalTwinFunctions';
import BaseVitalChart from '../../components/BaseVitalChart';
import {
    getBaseCanvasSettings,
    getBaseChartSettings,
    getBaseTooltipSettings,
    getBaseXAxisSettings,
    isBaseDataSeriesEmpty
} from '../../../../Helper/DigitalTwin/VitalChartConfigurations';
import TimeAxis from './BinarySignals/TimeAxis';


const VitalsChart = ({
    series,
    categories,
    startDate,
    isLoading,
    resetToLatestDataDate = () => { },
    chartID = "chartID",
    metrics,
    primaryGranularity,
    visibleSeries,
    onZoom,
    onZoomReset = () => {},
    zoomedRange = null,
    setLoading = null,
    isZoomed = false
}) => {

    const zoomResetViewRef = useRef(null);

    const { t } = useTranslation()

    const getChartSettings = () => {
        return getBaseChartSettings(chartID, onZoom, setLoading)
    }

    const getYAxisSettings = () => {
        const yAxisSettings = [];
        const swapedSeries = swapIndexForMultiYAxis(series)
        for (let i in swapedSeries) {
            let rightAxis = i > 1;
            let seriesItem = swapedSeries[i];
            let metric = metrics.find((metric) => metric.key == seriesItem.key) || {}
            let ranges = metric && metric.key ? metric.ranges : seriesItem.ranges

            ranges = calculateDynamicRange(seriesItem.data, ranges.min, ranges.max)

            let offsetX = -35;

            if (i / 2 == 0) {
                offsetX = -60;
            }

            offsetX = -15;

            yAxisSettings.push({
                seriesName: seriesItem.name,
                opposite: rightAxis,
                showAlways: true,
                tickAmount: 5,
                position: 'top',
                min: ranges.min,
                max: ranges.max,
                axisTicks: {
                    show: false,
                    borderType: 'dotted'
                },
                axisBorder: {
                    show: true,
                    color: metric.color,
                    offsetY: 0,
                    offsetX: -7
                },
                title: {
                    text: seriesItem.unit, // Y Axis Label
                    position: 'high',
                    rotate: 0,
                    offsetX: rightAxis ? -30 : 30,
                    offsetY: -160,
                    style: {
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#686868',
                    },
                },
                labels: {
                    show: true,
                    offsetX: offsetX,
                    offsetY: 0,
                    minWidth: 0,
                    maxWidth: 160,
                    style: {
                        colors: '#87939D',
                        fontSize: '10px',
                        fontWeight: 500,
                        fontfamily: 'Roboto !important',
                    },
                    formatter: (value) => Math.round(value),
                },
            })
        }

        // ensure yAxisSettings is always of length 4 so that it always renders 4 Y-axis
        // the ones with empty series are transparent. This is required to ensure X-axis
        // width remains constant irrespective of selected entities
        const hiddenYAxis = {
            seriesName: undefined,
            showAlways: true,
            tickAmount: 5,
            position: 'top',
            min: -100,
            max: 200,
            axisTicks: {
                show: false,
                borderType: 'dotted'
            },
            axisBorder: {
                show: true,
                color: 'transparent',
                offsetY: 0,
                offsetX: -7
            },
            title: {
                text: 'Y', // Y Axis Label
                position: 'high',
                rotate: 0,
                offsetY: -160,
                style: {
                    fontSize: '12px',
                    fontWeight: 400,
                    color: 'transparent',
                },
            },
            labels: {
                show: true,
                offsetY: 0,
                minWidth: 0,
                maxWidth: 160,
                style: {
                    colors: 'transparent',
                    fontSize: '10px',
                    fontWeight: 500,
                    fontfamily: 'Roboto !important',
                },
                formatter: (value) => Math.round(value),
            },
        }
        const yAxisSettingsCount = yAxisSettings.length;
        for (let index = 0; index < 4 - yAxisSettingsCount; index++) {
            const rightAxis = yAxisSettings.length >= 2;
            yAxisSettings.push({
                ...hiddenYAxis,
                opposite: rightAxis
            })
        }

        return yAxisSettings;
    }

    const getXAxisSettings = () => {
        const settings = getBaseXAxisSettings(primaryGranularity, categories, startDate, zoomedRange)
        return settings
    }

    const getToolTipSettings = () => {
        const tooltipSettings = getBaseTooltipSettings()
        tooltipSettings.custom = (opts) => getTooltipHtml(opts, metrics, t, visibleSeries)
        return tooltipSettings
    }

    const isDataSeriesEmpty = () => {
        return isBaseDataSeriesEmpty(series)
    }

    const getCanvasSettings = () => {
        return getBaseCanvasSettings(primaryGranularity, series)
    }

    const getChartOptions = useMemo(() => {
        return {
            ...getCanvasSettings(),
            chart: getChartSettings(),
            xaxis: getXAxisSettings(),
            yaxis: getYAxisSettings(),
            tooltip: getToolTipSettings(),
        };
    }, [series, categories, startDate, primaryGranularity, metrics, visibleSeries]);


    const getSeriesForChart = () => {
        if (series.length === 0) {
            return [{ data: [] }, { data: [] }, { data: [] }, { data: [] }]; // 4 empty series
        }
        if (series.length === 1) {
            return [...series, { data: [] }, { data: [] }, { data: [] }]; // 3 empty series
        }
        if (series.length === 2) {
            return [...series, { data: [] }, { data: [] }]; // 2 empty series
        }
        if (series.length === 3) {
            return [...series, { data: [] }]; // 1 empty series
        }
        return series;
    }

    if (isLoading) {
        return (
            <Box sx={{ height: 375, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CartLoader />
            </Box>
        );
    }

    const finalSeries = getSeriesForChart();

    return (
        <Box sx={{ height: 375 }}>
            <BaseVitalChart
                options={getChartOptions}
                series={finalSeries}
                chartID={chartID}
                chartHeight={360}
                primaryGranularity={primaryGranularity}
                resetToLatestDataDate={resetToLatestDataDate}
                isDataSeriesEmpty={isDataSeriesEmpty}
                zoomResetViewRef={zoomResetViewRef}
                onZoomReset={onZoomReset}
                isZoomed={isZoomed}
            />
        </Box>
    );
};

export default React.memo(VitalsChart);
